import React, { useState } from "react";
import FirstStep from "./FirstStep";
import SecondStep from "./SecondStep";
import ThirdStep from "./ThirdStep";
import FourthStep from "./FourthStep";
import { STEPS_PAYLOAD_INITIAL_STATE } from "utils/constants";
import {
  saveSignupProgress,
  getSignupProgress,
} from "utils/signupProgress";
import {
  BackgroundImage,
  InnerContainer,
  MainContainer,
  StyledSteps,
  StepsTitle,
} from "./styles";
import { useUpdateDropshipperMutation } from "api/authApi";
import cache from "utils/cache";
import { message } from "antd";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { useAuthUser } from "hooks/useAuthUser";

const ALL_STEP_LABELS = ["You", "Your Business", "Your Store", "Your Plan"];
const SPONSORED_STEP_LABELS = ["You", "Your Business", "Your Store"];

const PHONE_ALREADY_REGISTERED_MESSAGE =
  "This phone number is already registered.";

const PLAN_STEP_INDEX = 3;

const hasCompletedProfileStep = (user) =>
  Boolean(
    user?.firstName?.trim() &&
    user?.lastName?.trim() &&
    user?.phoneNumber?.trim()
  );

const resolveInitialStep = (user) => {
  const maxStep = user?.isSponsored ? 2 : PLAN_STEP_INDEX;
  if (!hasCompletedProfileStep(user)) {
    return 0;
  }

  const savedProgress = getSignupProgress(user?.id);
  if (savedProgress?.maxStep != null) {
    return Math.min(savedProgress.maxStep, maxStep);
  }

  return 1;
};

const SignUp = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuthUser();
  const showCancelledMessage = searchParams.get("cancelled") === "true";
  const isSponsored = Boolean(user?.isSponsored);
  const STEP_LABELS = isSponsored ? SPONSORED_STEP_LABELS : ALL_STEP_LABELS;

  const initialStep = resolveInitialStep(user);

  const [currentStep, setCurrentSteps] = useState(initialStep);
  const [maxStepReached, setMaxStepReached] = useState(initialStep);
  const [stepsPayload, setStepsPayload] = useState(() => {
    if (!user || !hasCompletedProfileStep(user)) {
      return STEPS_PAYLOAD_INITIAL_STATE;
    }
    return {
      ...STEPS_PAYLOAD_INITIAL_STATE,
      profileInfo: {
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
        phoneNumber: user.phoneNumber,
      },
    };
  });
  const [updateDropshipper, { isLoading }] = useUpdateDropshipperMutation();

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  if (!user?.firstTimeLogin) {
    return <Navigate to="/home" replace />;
  }

  const persistProgress = (step) => {
    saveSignupProgress(user?.id, step);
  };

  const handleSaveStoreInfo = async (finalPayload) => {
    const res = await updateDropshipper({
      ...finalPayload,
      completeOnboarding: isSponsored,
    });

    if (res.data) {
      cache.setItem("user", res?.data?.user);
      if (isSponsored) {
        message.success("Store details saved. Your sponsored account is ready.");
        navigate("/home", { replace: true });
        return;
      }
      persistProgress(PLAN_STEP_INDEX);
      setCurrentSteps(PLAN_STEP_INDEX);
      setMaxStepReached((prev) => Math.max(prev, PLAN_STEP_INDEX));
      message.success("Store details saved. Choose your plan to continue.");
    } else {
      const errorMessage =
        res.error?.data?.message ||
        res.error?.message ||
        "Something went wrong";
      message.error(errorMessage);
      if (errorMessage === PHONE_ALREADY_REGISTERED_MESSAGE) {
        setCurrentSteps(0);
      }
    }
  };

  const goToStep = (step) => {
    if (step >= 0 && step <= maxStepReached) {
      setCurrentSteps(step);
    }
  };

  const Next = () => {
    const nextStep = currentStep + 1;
    setCurrentSteps(nextStep);
    setMaxStepReached((prev) => {
      const updated = Math.max(prev, nextStep);
      persistProgress(updated);
      return updated;
    });
  };

  const Back = () => {
    if (currentStep > 0) {
      setCurrentSteps(currentStep - 1);
    }
  };

  const stepperItems = STEP_LABELS.map((label, index) => ({
    subTitle: <StepsTitle>{label}</StepsTitle>,
    disabled: index > maxStepReached,
  }));

  const stepContent = [
    <FirstStep
      key="profile"
      Next={Next}
      setStepsPayload={setStepsPayload}
      stepsPayload={stepsPayload}
    />,
    <SecondStep
      key="business"
      Next={Next}
      Back={Back}
      setStepsPayload={setStepsPayload}
      stepsPayload={stepsPayload}
    />,
    <ThirdStep
      key="store"
      Back={Back}
      setStepsPayload={setStepsPayload}
      stepsPayload={stepsPayload}
      handleUpdateDropshipper={handleSaveStoreInfo}
      isLoading={isLoading}
    />,
    ...(isSponsored
      ? []
      : [
          <FourthStep
            key="plan"
            Back={Back}
            showCancelledMessage={showCancelledMessage}
          />,
        ]),
  ];

  return (
    <BackgroundImage>
      <MainContainer>
        <InnerContainer>
          <StyledSteps
            current={currentStep}
            onChange={goToStep}
            labelPlacement="vertical"
            responsive={false}
            items={stepperItems}
          />
          {stepContent[currentStep]}
        </InnerContainer>
      </MainContainer>
    </BackgroundImage>
  );
};

export default SignUp;
